import { CommonModule, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import {
  NotificationService,
  PushNotifiactionData,
} from '@core/services/notification.service';
import { NotificationLogService } from '@core/services/notification-log.service';
import { Observable, Subscription } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Store } from '@ngxs/store';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Pagination } from '@core/models/pagination.model';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
import { IFILTER } from 'app/app.constants';
import { DropdownComponent, DropDownModel } from '@shared/ui/dropdown/dropdown.component';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

export type NotificationType = 'RICH' | 'INTERACTIVE' | 'SILENT' | 'ADS';
export type AudienceType = 'all' | 'custom';
export type ScheduleType = 'now' | 'later';

@Component({
  selector: 'app-push-notifications',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
    InputComponent,
    InputLabelComponent,
    PermissionDirective,
    FiltersComponent,
    TableSkeletonLoaderComponent,
    TableNavigatorComponent,
    TableRefreshComponent,
    DropdownComponent,
  ],
  providers: [TitleCasePipe],
  templateUrl: './push-notifications.component.html',
  styleUrl: './push-notifications.component.scss',
})
export class PushNotificationsComponent implements OnInit, OnDestroy {
  breadcrumbService = inject(BreadCrumbService);
  activatedRoute = inject(ActivatedRoute);
  fb = inject(FormBuilder);
  notificationService = inject(NotificationService);
  notificationLogService = inject(NotificationLogService);
  notify = inject(NotificationFacade);
  confirmDialogService = inject(ConfirmDialogService);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private router = inject(Router);
  titleCasePipe = inject(TitleCasePipe);

  subs: Subscription[] = [];

  selectedType: NotificationType = 'RICH';

  customFilters = [
    { label: 'Active users (last 7 days)', value: 'ACTIVE_USER' },
    { label: 'Inactive users (last 7 days)', value: 'INACTIVE_USER' },
    { label: 'New users (last 30 days)', value: 'NEW_USER' },
    { label: 'Users with incomplete profiles', value: 'INCOMPLETE_PROFILE_USER' },
    { label: 'Specific users', value: 'SPECIFIC_USERS' },
  ];

  // Notification-enabled users table (store-backed)
  notificationUsersList$!: Observable<any[]>;
  notificationUsersTotalCount$!: Observable<number>;
  isFetchingNotificationUsers$!: Observable<boolean>;

  accountFilterStructure: IFILTER[] = [
    {
      filterKey: 'account_type',
      label: 'Account Type',
      type: 'dropdown',
      dropdown: [
        { key: 'corporate', value: 'Corporate' },
        { key: 'individual', value: 'Individual' },
        { key: 'joint', value: 'Joint' },
      ],
    },
  ];

  selectedAccounts: any[] = [];

  adImageFiles: File[] = [];
  adImagePreviewUrls: string[] = [];
  isUploadingAd = false;

  adsList: any[] = [];
  adsTotalCount = 0;
  isFetchingAds = false;
  adsCurrentPage = 1;

  ngOnInit(): void {
    this.breadcrumbService.setPageBreadCrumb(this.activatedRoute);

    this.notificationUsersList$ = this.store.select(TableSelectors.getTableData('notificationUsers'));
    this.notificationUsersTotalCount$ = this.store.select(TableSelectors.getTableTotalCount('notificationUsers'));
    this.isFetchingNotificationUsers$ = this.store.select(TableSelectors.getTableLoading('notificationUsers'));

    this.listenToTypeChanges();
    this.listenToCustomFilterChanges();
    this.listenToNotificationUsersQueryParams();
  }

  // Form Group
  notificationForm = this.fb.group({
    notificationType: ['RICH', Validators.required],

    targetAudience: ['all', Validators.required],
    customFilters: [''],

    title: [''],
    message: ['', Validators.required],

    image_url: [''],

    action1_label: [''],
    action1_url: [''],
    action2_label: [''],
    action2_url: [''],

    // scheduleType: ['now'],
    // scheduledDate: [''],
    // scheduledTime: [''],
  });

  // getters
  get formControl() {
    return this.notificationForm.controls;
  }

  get targetAudience(): AudienceType {
    return this.formControl.targetAudience.value as AudienceType;
  }

  // listener
  private listenToTypeChanges() {
    this.formControl.notificationType.valueChanges
      .subscribe((type) => {
        this.selectedType = type as NotificationType;
        if (type !== 'ADS') {
          this.adImagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
          this.adImageFiles = [];
          this.adImagePreviewUrls = [];
        } else {
          if (this.activatedRoute.snapshot.queryParams['page']) {
            this.router.navigate([], {
              queryParams: { page: null },
              queryParamsHandling: 'merge',
            });
          }
          this.adsCurrentPage = 1;
          this.loadAds(1);
        }
        this.applyTypeValidators();
      });
  }

  private listenToCustomFilterChanges() {
    this.formControl.customFilters.valueChanges.subscribe((value) => {
      if (value === 'SPECIFIC_USERS') {
        if (this.activatedRoute.snapshot.queryParams['page']) {
          this.router.navigate([], {
            queryParams: { page: null },
            queryParamsHandling: 'merge',
          });
        } else {
          this.loadNotificationUsers();
        }
      }
    });
  }

  loadNotificationUsers(page = 1) {
    this.store.dispatch(new TableActions.LoadTableData({
      tableKey: 'notificationUsers',
      params: new Pagination({ skip: (page - 1) * 10, size: 10 }),
    }));
  }

  private listenToNotificationUsersQueryParams() {
    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      if (this.formControl.customFilters.value !== 'SPECIFIC_USERS') return;

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      let query = '';
      if (params['account_type']) query += `account_type=${params['account_type']}&`;
      if (params['search']) query += `search_text=${params['search']}&`;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.store.dispatch(new TableActions.FilterNotificationUsersList({ filter: query }));
      } else {
        this.store.dispatch(new TableActions.LoadTableData({
          tableKey: 'notificationUsers',
          params: new Pagination({ skip, size: take }),
        }));
      }
    });
    this.subs.push(sub);
  }

  navigateNotificationUsers(currentPage: number) {
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  isAccountSelected(account: any): boolean {
    return this.selectedAccounts.some(a => a.user_id === account.user_id);
  }

  toggleAccountSelection(account: any) {
    if (this.isAccountSelected(account)) {
      this.selectedAccounts = this.selectedAccounts.filter(a => a.user_id !== account.user_id);
    } else {
      if (this.selectedAccounts.length >= 5) {
        this.notify.error('You can only select up to 5 users.');
        return;
      }
      this.selectedAccounts.push(account);
    }
  }

  getAccountDisplayName(user: any): string {
    if (!user) return '-';
    const firstName = this.titleCasePipe.transform(user.first_name || '');
    const lastName = (user.last_name || '').toUpperCase();
    return `${firstName} ${lastName}`.trim() || '-';
  }

  getUserAccountNumbers(user: any): string {
    return user?.mango_account_number || '-';
  }

  getUserAccountTypes(user: any): string {
    return user?.account_type ? String(user.account_type).toUpperCase() : '-';
  }

  // Validators
  private applyTypeValidators() {
    const controls = [
      this.formControl.image_url,
      this.formControl.action1_label,
      this.formControl.action1_url,
      this.formControl.action2_label,
      this.formControl.action2_url,
    ];

    controls.forEach((c) => c.clearValidators());

    if (this.selectedType === 'ADS') {
      this.formControl.message.clearValidators();
      this.formControl.message.updateValueAndValidity();
      controls.forEach((c) => c.updateValueAndValidity());
      return;
    }

    this.formControl.message.setValidators(Validators.required);
    this.formControl.message.updateValueAndValidity();

    if (this.selectedType === 'INTERACTIVE') {
      // For interactive: action1_label and action1_url are required
      this.formControl.action1_label.setValidators(Validators.required);
      this.formControl.action1_url.setValidators(Validators.required);
      
      // For action2: if one is filled, both must be filled (but both are optional)
      this.formControl.action2_label.setValidators(
        this.conditionalRequiredValidator(() => !!this.formControl.action2_url.value)
      );
      this.formControl.action2_url.setValidators(
        this.conditionalRequiredValidator(() => !!this.formControl.action2_label.value)
      );
    }

    controls.forEach((c) => c.updateValueAndValidity());
  }

  private conditionalRequiredValidator(condition: () => boolean) {
    return (control: any) => {
      if (condition()) {
        return Validators.required(control);
      }
      return null;
    };
  }

  private isInteractiveValid() {
    if (this.selectedType !== 'INTERACTIVE') return true;

    const action1Complete = 
      this.formControl.action1_label.valid &&
      this.formControl.action1_url.valid;

    const action2Complete = 
      this.formControl.action2_label.valid &&
      this.formControl.action2_url.valid;

    // Require at least action1, but action2 is fully optional
    return action1Complete;
  }

  // Helper functions
  allFieldsValid(): boolean {
    if (this.selectedType === 'ADS') return this.adImageFiles.length > 0;
    return this.notificationForm.valid && this.isInteractiveValid();
  }

  selectType(type: NotificationType) {
    this.formControl.notificationType.setValue(type);
  }

  saveDraft() {
    this.sendPushNotification(this.buildPayload(), 'draft');
  }

  sendNotification() {
    if (!this.allFieldsValid()) return;

    this.sendPushNotification(this.buildPayload());
  }

  private buildPayload(): PushNotifiactionData {
    const value = this.notificationForm.value;
    const selectedFilter = value.customFilters;

    let audience: string[] = [];
    let specific_users: string[] | undefined = undefined;

    if (value.targetAudience === 'all') {
      audience = ['ALL_USER'];
    } else {
      if (selectedFilter === 'SPECIFIC_USERS') {
        audience = ['SPECIFIC_USERS'];
        specific_users = this.selectedAccounts.map(a => a.user_id);
      } else if (selectedFilter) {
        audience = [selectedFilter];
      }
    }

    const payload: any = {
      push_type: value.notificationType,
      audience,
      title: value.title,
      message: value.message,
      ...(specific_users ? {specific_users} : {})

      // schedule:
      //   v.scheduleType === 'now'
      //     ? { type: 'now' }
      //     : {
      //         type: 'later',
      //         date: v.scheduledDate,
      //         time: v.scheduledTime,
      //       },
    };

    if (value.notificationType === 'RICH') {
      payload.image_url = value.image_url;
    }

    if (value.notificationType === 'INTERACTIVE') {
      Object.assign(payload, {
        action1_label: value.action1_label,
        action1_url: value.action1_url,
      });
      
      // Only add action2 if both label and url are filled
      if (value.action2_label && value.action2_url) {
        Object.assign(payload, {
          action2_label: value.action2_label,
          action2_url: value.action2_url,
        });
      }
    }

    return payload;
  }

  sendPushNotification(
    notificationData: PushNotifiactionData,
    status?: string
  ) {
    const payload =
      status === 'draft'
        ? { ...notificationData, status: 'SAVE_AS_DRAFT' }
        : notificationData;
    const sub = this.notificationService
      .sendPushNotification(payload)
      .subscribe({
        next: (response) => {
          // Persist the log entry using payload + API response counts
          if (response?.data && status !== 'draft') {
            this.notificationLogService.addLog(notificationData, {
              success: response.data.success ?? 0,
              failed: response.data.failed ?? 0,
            });
          }
          this.notify.success('Push notification created successfully');
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'notificationLogs',
              params: new Pagination({ skip: 0, size: 10 }),
            })
          );
        },
        error: () => {
          this.notify.error('An error occurred while creating push notification');
        },
      });

    this.subs.push(sub);
  }

  onAdImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const incoming = Array.from(input.files);
    input.value = '';

    const valid = incoming.filter(f => {
      if (!['image/jpeg', 'image/png'].includes(f.type)) {
        this.notify.error(`${f.name}: only JPEG and PNG are accepted`);
        return false;
      }
      if (f.size > 2 * 1024 * 1024) {
        this.notify.error(`${f.name}: file must be under 2MB`);
        return false;
      }
      return true;
    });

    this.adImageFiles = [...this.adImageFiles, ...valid];
    this.adImagePreviewUrls = [...this.adImagePreviewUrls, ...valid.map(f => URL.createObjectURL(f))];
  }

  removeAdImage(index: number): void {
    URL.revokeObjectURL(this.adImagePreviewUrls[index]);
    this.adImageFiles.splice(index, 1);
    this.adImagePreviewUrls.splice(index, 1);
  }

  uploadAdImage(): void {
    if (this.adImageFiles.length === 0) return;
    const formData = new FormData();
    this.adImageFiles.forEach(f => formData.append('files', f));
    this.isUploadingAd = true;
    const sub = this.notificationService.uploadAdImage(formData).subscribe({
      next: () => {
        this.notify.success('Ad images uploaded successfully');
        setTimeout(() => window.location.reload(), 3000);
      },
      error: () => {
        this.isUploadingAd = false;
        this.notify.error('Failed to upload ad images');
      },
    });
    this.subs.push(sub);
  }

  loadAds(page = 1): void {
    this.isFetchingAds = true;
    const skip = (page - 1) * 10;
    const sub = this.notificationService.getAds(skip, 10).subscribe({
      next: (response) => {
        this.adsList = response.data?.data ?? [];
        this.adsTotalCount = response.data?.total_count ?? 0;
        this.isFetchingAds = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isFetchingAds = false;
        this.cdr.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  navigateAds(currentPage: number): void {
    this.adsCurrentPage = currentPage;
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
    this.loadAds(currentPage);
  }

  initAdsDropdownList(ad: any): DropDownModel[] {
    return [
      { title: 'View Image', action: () => window.open(ad.url, '_blank'), show: true },
      { title: 'Delete', action: () => this.confirmDeleteAd(ad), show: true },
    ];
  }

  private confirmDeleteAd(ad: any): void {
    this.confirmDialogService.openDialog({
      isOpen: true,
      isDialogLoading: false,
      title: 'Delete Ad Image',
      description: 'Are you sure you want to delete this ad image? This action cannot be undone.',
      loadingText: 'Deleting ad image...',
      bgConfirmButton: 'danger',
      action: () => this.deleteAd(ad.id),
    });
  }

  private deleteAd(id: string): void {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    const sub = this.notificationService.deleteAd(id).subscribe({
      next: () => {
        this.confirmDialogService.updateDialog({ isOpen: false });
        this.notify.success('Ad image deleted successfully');
        this.loadAds(this.adsCurrentPage);
      },
      error: () => {
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.notify.error('Failed to delete ad image');
      },
    });
    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}